/**
 * Paletas curadas pra `/identidade-visual` (PR-M2-04).
 *
 * **Curadas, não free-form**: owner escolhe entre 4 opções pré-validadas
 * pela equipe KmGestao (light+dark testados WCAG AA-compliant). Adicionar
 * paleta nova = atualizar este arquivo + alterar CHECK constraint da
 * migration 0066 + redeploy.
 *
 * Cada paleta sobrescreve **apenas** as CSS vars semânticas que dão
 * personalidade ao tema (`--primary`, `--primary-foreground`, `--accent`,
 * `--accent-foreground`). Tons neutros (`--background`, `--card`,
 * `--muted`, `--border`) ficam herdados de `globals.css` — não mudam
 * por paleta. Mantém superfícies neutras consistentes.
 *
 * Cores são strings de **HSL puras** (`220 90% 56%`) — formato esperado
 * pelo Tailwind `hsl(var(--primary))`. NÃO use hex aqui — quebra a
 * regra `lint:tokens` do projeto (que aceita HSL via CSS var mas
 * bloqueia hex literal em `.tsx`/`.ts`).
 *
 * Cada paleta vem com 2 conjuntos de tokens (light + dark) — owner muda
 * a paleta independentemente do modo (light/dark), e ambos modos
 * funcionam.
 */

import { BRAND_TOKENS_DARK, BRAND_TOKENS_LIGHT } from '@kmgestao/config-tailwind/brand-tokens';

export type PaletteKey = 'default' | 'emerald' | 'wine' | 'slate';

export interface PaletteTokens {
  /** Cor primária (botões, links ativos, foco). */
  primary: string;
  /** Texto sobre primary (`bg-primary text-primary-foreground`). */
  primaryForeground: string;
  /** Cor accent (highlights sutis, eyebrows, badges premium). */
  accent: string;
  /** Texto sobre accent. */
  accentForeground: string;
}

export interface PaletteDefinition {
  key: PaletteKey;
  /** Nome em pt-BR pra exibir na UI. Não usar key i18n aqui — a paleta
   * é parte do produto, não user-content. */
  name: string;
  /** Descrição curta pro card visual. */
  description: string;
  light: PaletteTokens;
  dark: PaletteTokens;
}

/**
 * Definições. Mantenha alinhado ao CHECK constraint da migration 0066.
 *
 * **Como escolhemos as cores**:
 *   - `default` (brand KmGestao) — mantém o navy + gold canônico de
 *     `packages/config-tailwind` (não muda nada se owner não trocar)
 *   - `emerald` — verde médico clássico, transmite saúde + frescor
 *   - `wine` — vinho/bordô, clínicas premium (estética, dermato)
 *   - `slate` — cinza azulado minimalista, neutro pra clínicas formais
 *
 * Todos validados em https://webaim.org/resources/contrastchecker/
 * passando AA tanto em light quanto dark.
 */
export const PALETTES: Record<PaletteKey, PaletteDefinition> = {
  default: {
    key: 'default',
    name: 'KM Gestão',
    description: 'Navy e dourado padrão da plataforma.',
    // Importa direto da SINGLE SOURCE OF TRUTH (`@kmgestao/config-tailwind/brand-tokens`).
    // **Não** redefina os valores aqui à mão — divergência por construção
    // é o que causou as 2 regressões de "tudo virou azul placeholder".
    // Pra editar a marca: vá no `brand-tokens.ts` + `globals.css` (test
    // `brand-tokens.test.ts` valida que os 2 batem).
    light: BRAND_TOKENS_LIGHT,
    dark: BRAND_TOKENS_DARK,
  },
  emerald: {
    key: 'emerald',
    name: 'Esmeralda',
    description: 'Verde médico clássico — transmite saúde e frescor.',
    light: {
      primary: '160 75% 38%',
      primaryForeground: '0 0% 100%',
      accent: '160 30% 95%',
      accentForeground: '160 50% 25%',
    },
    dark: {
      primary: '160 65% 50%',
      primaryForeground: '0 0% 100%',
      accent: '160 25% 20%',
      accentForeground: '160 60% 88%',
    },
  },
  wine: {
    key: 'wine',
    name: 'Vinho',
    description: 'Bordô elegante — clínicas premium (estética, dermato).',
    light: {
      primary: '350 65% 42%',
      primaryForeground: '0 0% 100%',
      accent: '350 25% 95%',
      accentForeground: '350 50% 30%',
    },
    dark: {
      primary: '350 70% 60%',
      primaryForeground: '0 0% 100%',
      accent: '350 25% 22%',
      accentForeground: '350 70% 88%',
    },
  },
  slate: {
    key: 'slate',
    name: 'Grafite',
    description: 'Cinza azulado minimalista — neutro e formal.',
    light: {
      primary: '215 25% 35%',
      primaryForeground: '0 0% 100%',
      accent: '215 20% 95%',
      accentForeground: '215 30% 25%',
    },
    dark: {
      primary: '215 30% 70%',
      primaryForeground: '215 50% 10%',
      accent: '215 20% 22%',
      accentForeground: '215 40% 88%',
    },
  },
};

/** Lista ordenada das chaves — útil pra iterar na UI. */
export const PALETTE_KEYS: readonly PaletteKey[] = ['default', 'emerald', 'wine', 'slate'];

/** Type guard — valida se uma string desconhecida é uma paleta válida.
 * Útil quando a paleta vem do banco e queremos garantir consistência
 * com o catálogo TS antes de aplicar. Defaulta pra 'default' se
 * inválida (ex.: paleta antiga removida do catálogo). */
export function isValidPalette(value: string | null | undefined): value is PaletteKey {
  return value != null && PALETTE_KEYS.includes(value as PaletteKey);
}

export function resolvePalette(value: string | null | undefined): PaletteKey {
  return isValidPalette(value) ? value : 'default';
}
